import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Brain, FileText, ShieldCheck, Zap, Crosshair, Map } from 'lucide-react';
import { FAQAccordion } from './components/FAQAccordion';
import { TestimonialCarousel } from './components/TestimonialCarousel';
import { Countdown } from './components/Countdown';
import { PurchasePopup } from './components/PurchasePopup';
const COVER_MURDOKU = 'https://cdn.magicpatterns.com/uploads/avzypTqfjyDGkvE2n5EqaY/PORTADA_MURDOKU.webp';
const COVER_VIAJE = 'https://cdn.magicpatterns.com/uploads/d3mRg8WCDVPjbfZzPNAShc/MURDOKU_VIAJE_AL_PASADO.webp';
const PACK_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/e21U2i9cTMQ8d8beuW1Z2B/MURDOKUS.png';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/4wzELUP6qiRd981MUJi4s6/IMG_8693.MOV';
const CHECKOUT_URL = 'https://murdoku.impultienda.ar/checkout/pack-murdoku';
export function App() {
  const [showFloatingBtn, setShowFloatingBtn] = useState(false);
  const [currency, setCurrency] = useState<'USD' | 'ARS'>('USD');
  useEffect(() => {
    fetch('https://api.country.is/').then(res => res.json()).then(data => {
      if (data.country === 'AR') {
        setCurrency('ARS');
      }
    }).catch(err => console.error('Geolocation error:', err));
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 600) {
        setShowFloatingBtn(true);
      } else {
        setShowFloatingBtn(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen font-sans text-murdoku-dark bg-murdoku-cream overflow-x-hidden" data-id="element-0" data-mp="38kgmeh0x00rm">
      
      {/* 1. Top Bar */}
      <div className="bg-murdoku-red text-white text-center py-3 px-4 shadow-md sticky top-0 z-50" data-id="element-1" data-mp="nvj3xh1mugq9">
        <div className="font-heading font-bold text-lg md:text-xl tracking-widest flex items-center justify-center gap-2" data-id="element-2" data-mp="sunf2d84aoj8">
          <Zap size={20} className="animate-pulse text-yellow-300" data-id="element-3" data-mp="35ox2qy1z4t4" />
          <span data-id="element-4" data-mp="3uct25gan0gyw">OFERTA DE LANZAMIENTO</span>
          <Countdown data-id="element-5" data-mp="3ubriwojgwo63" />
        </div>
        <div className="text-xs md:text-sm font-medium text-white/90 mt-1" data-id="element-6" data-mp="3uevqlvw5621i">
          Acceso digital inmediato después de tu compra
        </div>
      </div>

      {/* 2. Hero Section */}
      <section className="relative bg-murdoku-purple text-white pt-12 pb-20 overflow-hidden" data-id="element-7" data-mp="86clr5yt79k6">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-8" data-mp="3glnzs6fvxakv"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-murdoku-orange rounded-full blur-[100px] opacity-20 -translate-y-1/2 translate-x-1/3" data-id="element-9" data-mp="3bncp5thrmw1l"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-10" data-mp="3lkheu3atl9n5">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-11" data-mp="3liwlui45lyre">
            
            {/* Hero Image */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6
          }} className="order-1 md:order-2 flex justify-center w-full mx-auto" data-id="element-12" data-mp="2svtmxovkfm0m">
              <img src={PACK_IMAGE_URL} alt="Pack Murdoku Completo" className="w-full max-w-[500px] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" fetchPriority="high" data-id="element-13" data-mp="1d6awysw9maim" />
            </motion.div>

            {/* Hero Text */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-2 md:order-1 space-y-6" data-id="element-14" data-mp="1r3r11xr39s77">
              <h1 className="font-heading text-5xl md:text-7xl font-black leading-[1.1] text-white drop-shadow-md uppercase text-center md:text-left" data-id="element-15" data-mp="1m560fx7xd6i8">
                Dos libros.<br data-id="element-16" data-mp="6mum161wwokr" />
                <span className="text-murdoku-orange" data-id="element-17" data-mp="6qs5xtwbdk5p">160 misterios.</span><br data-id="element-18" data-mp="aqsita3rlnan" />
                ¿Podrás resolverlos todos?
              </h1>
              
              <p className="text-lg md:text-xl text-white/90 font-medium leading-relaxed text-center md:text-left" data-id="element-19" data-mp="qxxd3cjx6s2c">
                Descubrí el pack completo de Murdoku y poné a prueba tu lógica, atención y capacidad de deducción con 160 acertijos de crímenes y misterio.
              </p>

              <div className="bg-black/20 p-5 rounded-2xl border border-white/10" data-id="element-20" data-mp="c9anmi0kwjxe">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-base font-medium" data-id="element-21" data-mp="19wjdsqbiyh5q">
                  {['2 libros completos', '80 acertijos en cada libro', 'Soluciones incluidas', 'Descarga inmediata'].map((bullet, i) => <li key={i} className="flex items-start gap-2" data-id="element-22" data-mp="zwsiwnj1y054" data-mpi={i}>
                      <CheckCircle2 className="text-murdoku-orange shrink-0 mt-0.5" size={20} data-id="element-23" data-mp="vwie3e6zxfru" />
                      <span data-id="element-24" data-mp="3ko2osg6v2mqx">{bullet}</span>
                    </li>)}
                </ul>
              </div>

              <div className="pt-4 flex flex-col items-center md:items-start" data-id="element-25" data-mp="3gmpk49fi8pll">
                <div className="flex items-baseline gap-4 mb-3" data-id="element-26" data-mp="3gotxrj37h2z2">
                  <span className="text-5xl font-black text-murdoku-orange font-heading" data-id="element-27" data-mp="1w5zz0gfj90mi">
                    {currency === 'ARS' ? '$29.990' : 'USD 19,99'}
                  </span>
                  <span className="text-xl text-white/50 line-through decoration-murdoku-red decoration-2 font-bold" data-id="element-28" data-mp="25423j5yh6bej">
                    {currency === 'ARS' ? '$39.980' : 'USD 39,99'}
                  </span>
                </div>

                <a href="#compra" onClick={e => {
                e.preventDefault();
                document.getElementById('compra')?.scrollIntoView({
                  behavior: 'smooth'
                });
              }} className="w-full md:w-auto bg-murdoku-orange hover:bg-orange-500 text-white font-black text-xl md:text-2xl py-5 px-12 rounded-xl shadow-[0_8px_0_#C2410C] hover:shadow-[0_4px_0_#C2410C] hover:translate-y-1 transition-all duration-200 flex items-center justify-center uppercase font-heading tracking-wider text-center" data-id="element-29" data-mp="2ex0u2tdj8c0s">
                  QUIERO ACCEDER
                </a>

                <div className="flex items-center gap-2 mt-5 text-sm text-white/80 font-medium" data-id="element-30" data-mp="21toik2ur4x7t">
                  <ShieldCheck size={16} className="text-green-400" data-id="element-31" data-mp="1wuka5efnj3ij" />
                  <span data-id="element-32" data-mp="2at1tz12frvv6">Pago seguro · Producto digital · Acceso inmediato</span>
                </div>

                <div className="mt-4 inline-block bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg text-sm font-semibold text-center" data-id="element-33" data-mp="25se5am1ojr44">
                  ⚠️ No son muestras ni casos gratuitos: recibís ambos libros completos.
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Video Section */}
      

      {/* 4. Primer Libro */}
      <section className="py-20 bg-murdoku-cream relative overflow-hidden border-y border-gray-200" data-id="element-34" data-mp="2phy9fj6tob5t">
        <div className="max-w-6xl mx-auto px-6" data-id="element-35" data-mp="33h7ebmpkviuh">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-36" data-mp="2yicv4jnp7s9u">
            <div className="order-1 md:order-1 flex justify-center relative" data-id="element-37" data-mp="2yg09nwiqezs1">
              <div className="absolute inset-0 bg-murdoku-purple/5 rounded-full blur-3xl" data-id="element-38" data-mp="3i4eklf44bsn1"></div>
              <img src={COVER_MURDOKU} alt="Murdoku: 80 acertijos de lógica y asesinatos" className="w-full max-w-[450px] relative z-10 drop-shadow-2xl transform -rotate-2" data-id="element-39" data-mp="3n1y5uyz8h5oh" />
            </div>
            <div className="order-2 md:order-2 space-y-6" data-id="element-40" data-mp="3d2yyrku49i3l">
              <div className="inline-block bg-murdoku-purple text-white font-bold px-4 py-1.5 rounded-full text-sm tracking-wider uppercase" data-id="element-41" data-mp="2zclrlkp1u7fb">
                LIBRO COMPLETO · 80 ACERTIJOS
              </div>
              <h3 className="font-heading text-4xl md:text-6xl font-black text-murdoku-dark uppercase leading-none" data-id="element-42" data-mp="2pfqtv40lob23">
                Murdoku: <br data-id="element-43" data-mp="2uek3glcrnhwz" /><span className="text-murdoku-purple" data-id="element-44" data-mp="2uebsx0448ooy">80 acertijos de lógica y asesinatos</span>
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-45" data-mp="2ggdpuqv8weor">
                Analizá la ubicación de cada sospechoso y utilizá la lógica para reconstruir la escena del crimen. Solo una combinación conduce a la verdad.
              </p>
              <ul className="space-y-3" data-id="element-46" data-mp="2vw0xzr0e1w32">
                {['80 casos de dificultad progresiva.', 'Escenarios ilustrados y detallados.', 'Soluciones incluidas al final.'].map((item, i) => <li key={i} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-100 shadow-sm" data-id="element-47" data-mp="2ly4nn1f9z560" data-mpi={i}>
                    <Crosshair className="text-murdoku-purple shrink-0" size={20} data-id="element-48" data-mp="2hwri6sg3h3zm" />
                    <span className="font-semibold text-gray-800" data-id="element-49" data-mp="2d62pj9soi0yw">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Segundo Libro */}
      <section className="py-20 bg-murdoku-orange text-white relative overflow-hidden" data-id="element-50" data-mp="23852gpmk2a7v">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-51" data-mp="1y8ad4qifhsox"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-52" data-mp="1yad2d8brbhsl">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-53" data-mp="1u88d0lscd7br">
            <div className="order-2 md:order-1 space-y-6" data-id="element-54" data-mp="9pfpf9a15wr2">
              <div className="inline-block bg-white text-murdoku-orange font-bold px-4 py-1.5 rounded-full text-sm tracking-wider uppercase shadow-md" data-id="element-55" data-mp="9qz6v37faduf">
                LIBRO COMPLETO · 80 ACERTIJOS NUEVOS
              </div>
              <h3 className="font-heading text-4xl md:text-6xl font-black text-white uppercase leading-none" data-id="element-56" data-mp="nof9s4qifphf">
                Murdoku: <br data-id="element-57" data-mp="3m72rep5zk5np" /><span className="text-murdoku-dark" data-id="element-58" data-mp="3m7checr16cte">Viaje al Pasado</span>
              </h3>
              <p className="text-lg text-white/90 leading-relaxed" data-id="element-59" data-mp="3r4mgkk4822uf">
                Viajá a escenarios históricos llenos de secretos. Aplicá la lógica en cantinas, palacios y aldeas para descubrir al culpable.
              </p>
              <ul className="space-y-3" data-id="element-60" data-mp="4re2gd9hhdab">
                {['80 acertijos completamente nuevos.', 'Misterios en diferentes épocas.', 'No repite casos del primer libro.'].map((item, i) => <li key={i} className="flex items-center gap-3 bg-black/10 p-3 rounded-xl border border-white/10 backdrop-blur-sm" data-id="element-61" data-mp="38e4nufje3792" data-mpi={i}>
                    <Map className="text-white shrink-0" size={20} data-id="element-62" data-mp="2f6vhp72o7o5t" />
                    <span className="font-semibold text-white" data-id="element-63" data-mp="2k56rjhsytyo1">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="order-1 md:order-2 flex justify-center relative" data-id="element-64" data-mp="2p62r5j6aaynn">
              <img src={COVER_VIAJE} alt="Murdoku: Viaje al Pasado" className="w-full max-w-[450px] relative z-10 drop-shadow-2xl transform rotate-2" data-id="element-65" data-mp="2u332a8hzqrlm" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. Aclaración */}
      <section className="py-16 bg-murdoku-dark text-white relative" data-id="element-66" data-mp="21de0hsxginiy">
        <div className="max-w-5xl mx-auto px-6 text-center" data-id="element-67" data-mp="26diftfmmpc7l">
          <h2 className="font-heading text-4xl md:text-5xl font-black mb-6 uppercase text-murdoku-red" data-id="element-68" data-mp="2b5s12icsto3j">
            Recibís los libros completos
          </h2>
          <p className="text-xl md:text-2xl font-medium text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed border-b border-white/10 pb-10" data-id="element-69" data-mp="1dobgwfj9iklt">
            Este pack <span className="text-white font-bold" data-id="element-70" data-mp="3vyh29hjrukza">no contiene los casos gratuitos, demostraciones o muestras</span> que pueden encontrarse en internet. La compra incluye los dos libros completos, con 80 acertijos en cada uno y 160 desafíos en total.
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-71" data-mp="3hzzks1kgm4pt">
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-72" data-mp="3hzhh4jvgwo7v">
              <FileText className="mx-auto text-murdoku-orange mb-4" size={40} data-id="element-73" data-mp="3d1fzm3dowwyi" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-74" data-mp="389o4dfjla321">2 libros digitales completos</h4>
              <p className="text-gray-400 text-sm" data-id="element-75" data-mp="347jgkxfto0nb">No son capítulos sueltos ni versiones reducidas.</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-76" data-mp="2zaj5u98129pv">
              <Brain className="mx-auto text-murdoku-purple mb-4" size={40} data-id="element-77" data-mp="2uaesut6bvx4e" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-78" data-mp="2pbbxk0g5h59m">160 acertijos en total</h4>
              <p className="text-gray-400 text-sm" data-id="element-79" data-mp="2fnpsyq85zhxh">80 casos en cada libro listos para resolver.</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-80" data-mp="2kn2gld6v0t14">
              <CheckCircle2 className="mx-auto text-green-400 mb-4" size={40} data-id="element-81" data-mp="26n1czpz7n7hy" />
              <h4 className="font-heading font-bold text-2xl mb-2" data-id="element-82" data-mp="26p4cr1q60ezv">Soluciones incluidas</h4>
              <p className="text-gray-400 text-sm" data-id="element-83" data-mp="2aqfvcnamfzyk">Podrás comprobar cada investigación al final.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Ideal Para */}
      <section className="py-20 bg-white border-t border-gray-200" data-id="element-84" data-mp="33c8zb07nlkmx">
        <div className="max-w-4xl mx-auto px-6" data-id="element-85" data-mp="2pkkrh94ftibr">
          <div className="bg-murdoku-purple text-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-86" data-mp="2pjt2wi99ckuz">
            <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-87" data-mp="3hd1vrqhimpso"></div>
            <div className="relative z-10" data-id="element-88" data-mp="3hayvmdmnfga8">
              <h2 className="font-heading text-4xl md:text-5xl font-black mb-8 text-center uppercase text-murdoku-orange" data-id="element-89" data-mp="3m4jyok33n0ga">
                Este pack es para vos si…
              </h2>
              <ul className="grid md:grid-cols-2 gap-4 text-lg font-medium" data-id="element-90" data-mp="15pztqkd6pf04">
                {['Te gustan los sudokus y juegos de lógica.', 'Disfrutás los misterios y casos criminales.', 'Querés entrenar la mente mientras te entretenés.', 'Te gusta resolver desafíos con amigos o en pareja.'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-black/20 p-4 rounded-xl" data-id="element-91" data-mp="rp5h1pj3srvn" data-mpi={i}>
                    <CheckCircle2 className="text-murdoku-orange shrink-0 mt-0.5" size={24} data-id="element-92" data-mp="mqu8di4jonf0" />
                    <span data-id="element-93" data-mp="11nlaw09imf8u">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 11. Testimonios */}
      <section className="py-20 bg-murdoku-cream overflow-hidden" data-id="element-94" data-mp="wquutcxpnjnf">
        <div className="max-w-6xl mx-auto px-6 text-center mb-12" data-id="element-95" data-mp="2d1p1qxkt3n9o">
          <h2 className="font-heading text-4xl md:text-5xl font-black text-murdoku-dark mb-4 uppercase" data-id="element-96" data-mp="284gisuru3f7d">
            Lo que dicen quienes ya lo compraron
          </h2>
        </div>
        <TestimonialCarousel data-id="element-97" data-mp="mlwmj5xelonb" />
      </section>

      {/* 12. CTA Final */}
      <section id="compra" className="py-24 bg-gradient-to-b from-murdoku-purple to-murdoku-dark text-white relative overflow-hidden" data-id="element-98" data-mp="mo0mjcs6m3ib">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-99" data-mp="vmcouc2eupql"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-100" data-mp="vk89mxz2a4b0">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-101" data-mp="10kw63z627dcm">
            
            <div className="w-[90%] md:w-[50%] mx-auto order-1 flex justify-center" data-id="element-102" data-mp="15jqt7db27o2j">
              <img src={PACK_IMAGE_URL} alt="Pack Murdoku Completo" className="w-full max-w-[500px] object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.8)]" loading="lazy" data-id="element-103" data-mp="1ab8slwxfpgle" />
            </div>

            <div className="w-full md:w-[50%] text-center md:text-left order-2" data-id="element-104" data-mp="1jcp09wwv74lx">
              <h2 className="font-heading text-5xl md:text-6xl font-black mb-6 text-white uppercase leading-[1.1]" data-id="element-105" data-mp="1ob0c2d6pzp6s">
                Dos aventuras completas.<br data-id="element-106" data-mp="1yfzwllku9y6o" />
                <span className="text-murdoku-orange" data-id="element-107" data-mp="1ygi4jfgv60uf">160 crímenes por resolver.</span>
              </h2>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl w-full" data-id="element-108" data-mp="1ti6v35uhdscq">
                <div className="flex items-baseline justify-center md:justify-start gap-4 mb-2" data-id="element-109" data-mp="1oiu7givsch93">
                  <span className="text-6xl font-black text-murdoku-orange font-heading" data-id="element-110" data-mp="1khs5huahhr3f">
                    {currency === 'ARS' ? '$29.990' : 'USD 19,99'}
                  </span>
                  <span className="text-2xl text-white/50 line-through decoration-murdoku-red decoration-2 font-bold" data-id="element-111" data-mp="2c7de6tz6slz2">
                    {currency === 'ARS' ? '$39.980' : 'USD 39,99'}
                  </span>
                </div>
                <p className="text-sm text-white/80 font-medium mb-8 text-center md:text-left" data-id="element-112" data-mp="2zw66mgglobox">
                  Precio especial del pack
                </p>

                <a href={CHECKOUT_URL} className="block w-full bg-murdoku-orange hover:bg-orange-500 text-white font-black text-xl md:text-2xl py-5 px-8 rounded-xl shadow-[0_8px_0_#C2410C] hover:shadow-[0_4px_0_#C2410C] hover:translate-y-1 transition-all duration-200 uppercase text-center font-heading tracking-wider" data-id="element-113" data-mp="2v3wf4vzu73kj">
                  QUIERO EL PACK
                </a>

                <div className="flex flex-col items-center md:items-start gap-3 mt-6 text-sm text-white/80 font-medium" data-id="element-114" data-mp="og5mgdtjlvzj">
                  <div className="flex items-center gap-2" data-id="element-115" data-mp="afuvlas8ghed">
                    <ShieldCheck size={18} className="text-green-400" data-id="element-116" data-mp="fh23rayafilk" />
                    <span data-id="element-117" data-mp="j9nuhotws6u">Producto digital · Acceso inmediato · Pago seguro</span>
                  </div>
                  <div className="text-xs text-white/50" data-id="element-118" data-mp="5icqjvhc6mbn">
                    ⚠️ No se envía ningún producto físico. Los archivos se entregan digitalmente.
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 13. FAQ */}
      <section className="py-20 bg-white" data-id="element-119" data-mp="3ke52pqm0a1fr">
        <div className="max-w-4xl mx-auto px-6" data-id="element-120" data-mp="3kf6btpjgoduu">
          <h2 className="font-heading text-4xl md:text-5xl font-black text-center text-murdoku-dark mb-12 uppercase" data-id="element-121" data-mp="3ffblbn3llqab">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-122" data-mp="3u5ixjl30id2k" />
        </div>
      </section>

      {/* 14. Footer */}
      <footer className="bg-murdoku-dark text-white/70 py-16 border-t border-white/10" data-id="element-123" data-mp="3pcptz0ja5as2">
        <div className="max-w-6xl mx-auto px-6" data-id="element-124" data-mp="6ygx9drgmaga">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-10 mb-12" data-id="element-125" data-mp="71cqiosd4aye">
            <div className="text-center md:text-left max-w-md" data-id="element-126" data-mp="gxfum06ryjnz">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-127" data-mp="gwwln8z3r5m3">
                <div className="bg-murdoku-purple p-2 rounded-lg" data-id="element-128" data-mp="bzeb5e5fsibz">
                  <Brain className="text-murdoku-orange" size={24} data-id="element-129" data-mp="2iq40khaszl7g" />
                </div>
                <h3 className="font-heading font-bold text-3xl text-white tracking-wide uppercase" data-id="element-130" data-mp="2dqrg237emq81">
                  Atlas Digital Hub
                </h3>
              </div>
              <p className="text-lg text-white/90 font-medium mb-2" data-id="element-131" data-mp="1wvvk7kg2r0l8">
                Productos digitales para aprender, crear y entretenerte
              </p>
              <p className="text-sm text-white/50 mb-4" data-id="element-132" data-mp="21n3usjt54rzz">
                Tienda digital de recursos, libros y productos descargables
              </p>
            </div>

            <div className="flex flex-col items-center md:items-end gap-4 text-sm" data-id="element-133" data-mp="18yputyihrlls">
              <a href="#" className="hover:text-white transition-colors" data-id="element-134" data-mp="1dwhq92ichpuq">Términos y Condiciones</a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-135" data-mp="1ivkuikqi3s1n">Políticas de Privacidad</a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-136" data-mp="1nvqhvopqmsdx">Contacto</a>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 text-center text-xs text-white/40 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-137" data-mp="2ua7wofwxsq7j">
            <p data-id="element-138" data-mp="nn0tbzimh9al">© {new Date().getFullYear()} Atlas Digital Hub. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      <PurchasePopup data-id="element-139" data-mp="ioyyyifqxnjt" />
    </div>;
}